import {Button, type ButtonProps} from '@primer/react'
import {CopilotIcon, FileCheckIcon, TriangleDownIcon} from '@primer/octicons-react'
import {IconButtonSelector} from '@github-ui/copilot-chat/components/IconButtonSelector'
import {ToolbarButtonSkeleton} from '@github-ui/copilot-chat/components/ToolbarButtonSkeleton'
import {GitHubAvatar} from '@github-ui/github-avatar'
import type {SubAgent} from '../../types/agent'
import type {AgentInfo} from '../../types/session'
import styles from './AgentSelector.module.css'
import {clsx} from 'clsx'

// Props that Primer's `ActionMenu.Anchor` clones onto its trigger child (onClick /
// keyboard handlers / aria wiring / id). The trigger must forward these to the real
// button so the menu opens; kept narrow so it composes with `IconButtonSelector`'s
// strict aria-label typing.
type AnchorInjectedProps = {
  onClick?: React.MouseEventHandler<HTMLButtonElement>
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup']
  'aria-expanded'?: React.AriaAttributes['aria-expanded']
  id?: string
}

type AgentSelectorMenuTriggerProps = {
  isLoading: boolean
  isCopilotSelected: boolean
  selectedAgentObj: SubAgent | AgentInfo | undefined
  agentType: string | undefined
  ariaLabel: string
  disabled: boolean
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
  /** When true, the icon trigger renders a trailing dropdown caret (icon + ▾). */
  showDropdownCaret?: boolean
  ref?: React.Ref<HTMLButtonElement>
} & AnchorInjectedProps

// Icon trigger with a trailing dropdown caret (icon + ▾). The icon is the visible
// child; the accessible name comes from `aria-label`, so the caret stays decorative.
function CaretIconTrigger({
  iconNode,
  ariaLabel,
  disabled,
  variant,
  size,
  className,
  ref,
  ...rest
}: {
  iconNode: React.ReactNode
  ariaLabel: string
  disabled: boolean
  variant?: ButtonProps['variant']
  size?: ButtonProps['size']
  className?: string
  ref?: React.Ref<HTMLButtonElement>
} & AnchorInjectedProps) {
  return (
    <Button
      type="button"
      trailingAction={TriangleDownIcon}
      aria-label={ariaLabel}
      disabled={disabled}
      variant={variant}
      size={size}
      className={clsx(styles.caretAnchor, className)}
      ref={ref}
      {...rest}
    >
      <span className={styles.iconBox}>{iconNode}</span>
    </Button>
  )
}

export function AgentSelectorMenuTrigger({
  isLoading,
  isCopilotSelected,
  selectedAgentObj,
  agentType,
  ariaLabel,
  disabled,
  variant,
  size,
  className,
  showDropdownCaret = false,
  ref,
  ...rest
}: AgentSelectorMenuTriggerProps) {
  if (isLoading) {
    // Match the loaded control's footprint so neighbors don't shift when loading resolves.
    // Icon-only triggers are square; caret triggers add the trailing dropdown glyph and padding.
    const controlSize = size === 'small' ? 'var(--control-small-size)' : 'var(--control-medium-size)'
    const skeletonWidth = showDropdownCaret ? `calc(${controlSize} + var(--base-size-20))` : controlSize
    return (
      <ToolbarButtonSkeleton
        width={skeletonWidth}
        height={controlSize}
        className={className}
        aria-label="Loading agents"
        data-testid="agent-selector-skeleton"
      />
    )
  }
  // Special case for Copilot: normally use the theme-aware icon instead of an avatar so it can
  // change with dark mode, but when showDropdownCaret is set and an avatar_url is available, the
  // caret trigger below renders that avatar instead of the icon.
  if (isCopilotSelected) {
    const copilotAvatarUrl =
      selectedAgentObj && typeof selectedAgentObj === 'object' && 'avatar_url' in selectedAgentObj
        ? selectedAgentObj.avatar_url
        : undefined
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={
          copilotAvatarUrl ? <GitHubAvatar src={copilotAvatarUrl} size={16} square /> : <CopilotIcon size={16} />
        }
        ariaLabel={ariaLabel}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      />
    ) : (
      <IconButtonSelector
        icon={CopilotIcon}
        selectedIcon={CopilotIcon}
        aria-label={ariaLabel}
        selected={false}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      />
    )
  }
  // custom agent with file check icon
  if (selectedAgentObj && typeof selectedAgentObj === 'object' && agentType === 'customSubAgent') {
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={<FileCheckIcon size={16} />}
        ariaLabel={ariaLabel}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      />
    ) : (
      <IconButtonSelector
        icon={CopilotIcon}
        selectedIcon={FileCheckIcon}
        aria-label={ariaLabel}
        selected={Boolean(selectedAgentObj)}
        disabled={disabled}
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      />
    )
  }
  // 3rd-party agent with avatar
  if (selectedAgentObj && typeof selectedAgentObj === 'object' && 'avatar_url' in selectedAgentObj) {
    return showDropdownCaret ? (
      <CaretIconTrigger
        iconNode={<GitHubAvatar src={selectedAgentObj.avatar_url} size={16} square />}
        ariaLabel={ariaLabel}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        className={className}
        ref={ref}
        {...rest}
      />
    ) : (
      <Button
        aria-label={ariaLabel}
        disabled={disabled || isLoading}
        variant={variant}
        size={size}
        className={clsx(styles.avatarButton, className)}
        ref={ref}
        {...rest}
      >
        <GitHubAvatar src={selectedAgentObj.avatar_url} size={18} square />
      </Button>
    )
  }
  // Fallback for any other case (e.g., topLevel agent without avatar_url - this should never happen)
  return showDropdownCaret ? (
    <CaretIconTrigger
      iconNode={selectedAgentObj ? <FileCheckIcon size={16} /> : <CopilotIcon size={16} />}
      ariaLabel={ariaLabel}
      disabled={disabled}
      variant={variant}
      size={size}
      className={className}
      ref={ref}
      {...rest}
    />
  ) : (
    <IconButtonSelector
      icon={CopilotIcon}
      selectedIcon={FileCheckIcon}
      aria-label={ariaLabel}
      selected={Boolean(selectedAgentObj)}
      disabled={disabled}
      variant={variant}
      size={size}
      className={className}
      ref={ref}
      {...rest}
    />
  )
}
